import { useLocation } from "react-router";
import { NavigationTrigger, ReaderNavigationContext } from "../types";
import { useMemo } from "react";
import { useEndInterstitial } from "./useEndInterstital";
import { Section } from "../../../types";
import { useCurrentCMSStream } from "../../../hooks/useCurrentCMSStream";
import { getCurrentArea } from "./readerNavigationArea";
import { deriveCanonicalStreamSource } from "./readerNavigationStream";

export interface UseGetReaderNavigationContextProps {
  trigger?: NavigationTrigger;
  contextComicId?: number;
  sectionPosition?: number;
  cardPosition?: number;
  section?: Section;
  notificationTypeId?: number;
  area?: string;
}

export function useGetReaderNavigationContext(
  {
    trigger,
    contextComicId,
    section,
    cardPosition,
    sectionPosition,
    notificationTypeId,
    area,
  }: UseGetReaderNavigationContextProps,
  dependencies: ReadonlyArray<unknown> = [],
) {
  const location = useLocation();
  const { showEndInterstitial } = useEndInterstitial();
  const currentArea =
    area ?? getCurrentArea(location.pathname, !!showEndInterstitial);
  const stream = useCurrentCMSStream(currentArea);
  const normalizedStream = deriveCanonicalStreamSource({
    pathname: location.pathname,
    area: currentArea,
    streamId: stream?.id,
    streamSlug: stream?.slug,
  });

  // The caller controls any extra dependency list used to invalidate the memoized context.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo<ReaderNavigationContext>(() => {
    let q = undefined;
    let searchQueryId = undefined;
    let searchTab = undefined;

    if (currentArea === "search") {
      const queryParams = new URLSearchParams(location.search);
      q = queryParams.get("q") ?? undefined;
      searchQueryId = queryParams.get("query_id") ?? undefined;
      const isInArtistTab = location.pathname.includes("/creators");
      const isInReleasesTab = location.pathname.includes("/books");
      if (isInArtistTab) {
        searchTab = "artist";
      } else if (isInReleasesTab) {
        searchTab = "release";
      } else {
        searchTab = "comic";
      }
    }

    return {
      area: currentArea,
      comicId: contextComicId,
      trigger,
      sectionPosition,
      cardPosition,
      sectionId: section?.id,
      sectionType: section?.source_type,
      q,
      searchQueryId,
      stream: normalizedStream,
      notificationTypeId,
      searchTab,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArea, trigger, contextComicId, cardPosition, sectionPosition, section?.id, section?.source_type, notificationTypeId, normalizedStream, location.search, location.pathname, ...dependencies]);
}
